import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import { usersAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Edit2, Trash2, KeyRound, Shield, Smartphone, Loader2, X } from 'lucide-react';

const EMPTY_FORM = { name: '', email: '', password: '', role: 'surveyor' };
const EMPTY_EDIT = { name: '', email: '', role: '', password: '' };

export const AdminUsers = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Create dialog
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_FORM);

    // Edit dialog
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState(EMPTY_EDIT);
    const [showPasswordField, setShowPasswordField] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const res = await usersAPI.list();
            setUsers(res.data);
        } catch { toast.error('Failed to load users'); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        if (!createForm.name.trim() || !createForm.email.trim() || !createForm.password) {
            toast.error('Name, email and password are required');
            return;
        }
        setSaving(true);
        try {
            const res = await usersAPI.create(createForm);
            setUsers(prev => [...prev, res.data]);
            setCreateOpen(false);
            setCreateForm(EMPTY_FORM);
            toast.success(`${res.data.name} added`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to create user');
        } finally { setSaving(false); }
    };

    const openEdit = (u) => {
        setEditUser(u);
        setEditForm({ name: u.name, email: u.email, role: u.role, password: '' });
        setShowPasswordField(false);
    };

    const handleUpdate = async () => {
        if (!editForm.name.trim() || !editForm.email.trim()) {
            toast.error('Name and email are required');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: editForm.name,
                email: editForm.email,
                role: editForm.role,
            };
            if (editForm.password) payload.password = editForm.password;
            const res = await usersAPI.update(editUser.id, payload);
            setUsers(prev => prev.map(u => u.id === editUser.id ? res.data : u));
            setEditUser(null);
            toast.success('User updated');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to update');
        } finally { setSaving(false); }
    };

    const handleDelete = async (userId) => {
        try {
            await usersAPI.delete(userId);
            setUsers(prev => prev.filter(u => u.id !== userId));
            toast.success('User removed');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to delete');
        }
    };

    if (loading) return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-48 rounded-xl" />
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">Users</h1>
                        <p className="text-slate-500 text-sm">Manage admin and surveyor accounts</p>
                    </div>
                    <Button onClick={() => { setCreateForm(EMPTY_FORM); setCreateOpen(true); }}
                        className="h-10 bg-red-600 hover:bg-red-700 text-white" data-testid="add-user-btn">
                        <Plus className="h-4 w-4 mr-2" /> Add User
                    </Button>
                </div>

                {/* Users list */}
                <Card className="bg-white border-slate-200">
                    <CardContent className="p-0">
                        <div className="divide-y divide-slate-100">
                            {users.map(u => (
                                <div key={u.id} className="flex items-center gap-3 px-4 py-3.5" data-testid={`user-row-${u.id}`}>
                                    {/* Avatar */}
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${u.role === 'admin' ? 'bg-red-100' : 'bg-blue-100'}`}>
                                        {u.role === 'admin'
                                            ? <Shield className="h-5 w-5 text-red-600" />
                                            : <Smartphone className="h-5 w-5 text-blue-600" />}
                                    </div>
                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-sm text-slate-900 truncate">{u.name}</p>
                                            {u.id === currentUser?.id && (
                                                <span className="text-[10px] bg-slate-100 text-slate-500 font-semibold px-1.5 py-0.5 rounded uppercase">You</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                                    </div>
                                    {/* Role badge */}
                                    <Badge className={u.role === 'admin' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                                        {u.role}
                                    </Badge>
                                    {/* Actions */}
                                    <div className="flex gap-1 shrink-0">
                                        <button onClick={() => openEdit(u)}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                            data-testid={`edit-user-${u.id}`}>
                                            <Edit2 className="h-3.5 w-3.5" />
                                        </button>
                                        {u.id !== currentUser?.id && (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <button className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                        data-testid={`delete-user-${u.id}`}>
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            They will no longer be able to log in. This cannot be undone.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => handleDelete(u.id)} className="bg-red-600 hover:bg-red-700">
                                                            Remove
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* ── Create User Dialog ── */}
                <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Full Name *</Label>
                                <Input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                                    placeholder="e.g. John Smith" className="h-11 border-slate-300" data-testid="create-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Email Address *</Label>
                                <Input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))}
                                    placeholder="john@company.co.uk" className="h-11 border-slate-300" data-testid="create-email-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Password *</Label>
                                <Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                                    placeholder="Set a secure password" className="h-11 border-slate-300" data-testid="create-password-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Role *</Label>
                                <div className="flex gap-2">
                                    {['surveyor', 'admin'].map(role => (
                                        <button key={role} type="button"
                                            onClick={() => setCreateForm(p => ({ ...p, role }))}
                                            className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold border transition-colors capitalize ${
                                                createForm.role === role
                                                    ? role === 'admin' ? 'bg-red-600 text-white border-red-600' : 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                                            }`}>
                                            {role === 'surveyor' ? 'Surveyor (Mobile)' : 'Admin'}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-slate-400">
                                    {createForm.role === 'surveyor' ? 'Can create and manage surveys only' : 'Full access to all settings and admin tools'}
                                </p>
                            </div>
                            <Button onClick={handleCreate} disabled={saving} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-create-btn">
                                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Creating...</> : 'Create User'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* ── Edit User Dialog ── */}
                <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>Edit — {editUser?.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-2">
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Full Name</Label>
                                <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                    className="h-11 border-slate-300" data-testid="edit-name-input" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-bold uppercase text-slate-500">Email Address</Label>
                                <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))}
                                    className="h-11 border-slate-300" data-testid="edit-email-input" />
                            </div>
                            {/* Role — can't change your own role */}
                            {editUser?.id !== currentUser?.id && (
                                <div className="space-y-1.5">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Role</Label>
                                    <div className="flex gap-2">
                                        {['surveyor', 'admin'].map(role => (
                                            <button key={role} type="button"
                                                onClick={() => setEditForm(p => ({ ...p, role }))}
                                                className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold border transition-colors ${
                                                    editForm.role === role
                                                        ? role === 'admin' ? 'bg-red-600 text-white border-red-600' : 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                                                }`}>
                                                {role === 'surveyor' ? 'Surveyor' : 'Admin'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Password reset */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Password</Label>
                                    <button onClick={() => setShowPasswordField(!showPasswordField)}
                                        className="text-xs text-red-600 font-semibold flex items-center gap-1 hover:underline">
                                        <KeyRound className="h-3 w-3" />
                                        {showPasswordField ? 'Cancel reset' : 'Reset password'}
                                    </button>
                                </div>
                                {showPasswordField ? (
                                    <Input type="password" value={editForm.password}
                                        onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))}
                                        placeholder="Enter new password"
                                        className="h-11 border-slate-300" data-testid="edit-password-input" />
                                ) : (
                                    <p className="text-xs text-slate-400 px-1">Click "Reset password" to set a new password</p>
                                )}
                            </div>
                            <Button onClick={handleUpdate} disabled={saving} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-edit-btn">
                                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</> : 'Save Changes'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
};
